import { Link as RouterLink } from 'react-router-dom';

import { AppIcon } from '@backrs_ui/common-ui';

import { Box, Chip, SxProps, Theme } from '@mui/material';

import { type KeyLever } from '../keyLevers.content';
import { isExternalUrl, leverLinksFor, toKebabId } from '../keyLevers.utils';

const linkProps = (url: string) =>
  isExternalUrl(url)
    ? { component: 'a', href: url, target: '_blank', rel: 'noreferrer' }
    : { component: RouterLink, to: url };

export const KeyLeverLinks = ({ lever }: Readonly<{ lever: KeyLever }>) => {
  const chips = leverLinksFor(lever);

  if (!chips.length) return null;

  return (
    <Box sx={rowSx}>
      {chips.map(({ label, url }) => {
        const id = `key-lever-${lever.id}-link-${toKebabId(label)}`;

        if (!url) {
          return (
            <Chip key={label} id={id} variant="pill" size="md" label={label} sx={plainChipSx} />
          );
        }

        return (
          <Chip
            key={label}
            id={id}
            variant="pill"
            size="md"
            type="trailing"
            label={label}
            icon={<AppIcon iconName="ArrowUpRight" diameter="12px" color="Brand/700" />}
            clickable
            sx={linkChipSx}
            {...linkProps(url)}
          />
        );
      })}
    </Box>
  );
};

const rowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: backrs.spacing['spacing-sm'],
});

const plainChipSx: SxProps<Theme> = ({ backrs, typography }) => ({
  backgroundColor: backrs.colors['Colors/Background/bg-secondary'],
  border: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  '& .MuiChip-label': {
    ...typography['text-xs-medium'],
    color: backrs.colors['Colors/Text/text-secondary'],
  },
});

const linkChipSx: SxProps<Theme> = ({ backrs, typography }) => ({
  backgroundColor: backrs.colors['Brand/50'],
  border: `1px solid ${backrs.colors['Brand/200']}`,
  textDecoration: 'none',
  '& .MuiChip-label': {
    ...typography['text-xs-medium'],
    color: backrs.colors['Brand/700'],
  },
  '&:hover': {
    backgroundColor: backrs.colors['Brand/100'],
  },
});
